import TaskForm from '../components/TaskForm';
import { Link, useSearchParams } from 'react-router-dom';
import { UseQueryOptions, UseQueryResult, useQueries, useQuery } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import { ItemApiResponse, Patient, Pharmacy, url, client } from '../api';
import styled from 'styled-components';
import { useState } from 'react';
import { Cards, PatientCard, PharmacyCard, TandemCard } from '../components/cards';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
const getWorkItem = (itemId: string) => () =>
  client.get(url(`/queue-item/${itemId}`)).then((res) => {
    return res.data;
  });

const WorkItems = () => {
  const [searchParams] = useSearchParams();
  const itemIds = searchParams.getAll('itemId');

  const itemIdQueryString = itemIds.map((itemId) => `itemId=${itemId}`).join('&');

  const sharedInfoQuery = useQuery<{ pharmacy: Pharmacy; patient: Patient }, AxiosError>({
    queryKey: ['sharedInfo', itemIds],
    queryFn: () =>
      axios
        .get(url(`/shared-info?${itemIdQueryString}`), {
          withCredentials: true,
        })
        .then((res) => res.data),
  });

  const workItemQueries = useQueries({
    queries: itemIds.map<UseQueryOptions<ItemApiResponse>>((itemId) => ({
      queryKey: ['workItem', itemId],
      queryFn: getWorkItem(itemId),
      cacheTime: 100,
    })),
  });

  if (sharedInfoQuery.isLoading) {
    return <LoadingSpinner />;
  }

  if (sharedInfoQuery.isError) {
    if (sharedInfoQuery.error.response?.status === 400) {
      return (
        <div>
          <p>
            The tasks that have been grouped together are not compatible. This is likely due to a data issue. For now,
            you should be able to access them through separate links.
          </p>
          <ul>
            {itemIds.map((itemId) => (
              <li key={itemId}>
                <Link to={`/items?itemId=${itemId}`} target="_blank">
                  {itemId}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      );
    }
    return <div>Error: Unknown Error</div>;
  }

  return (
    <div>
      <Cards>
        {sharedInfoQuery.data && sharedInfoQuery.data.pharmacy && (
          <PharmacyCard pharmacy={sharedInfoQuery.data.pharmacy} />
        )}
        {sharedInfoQuery.data && sharedInfoQuery.data.patient && <PatientCard patient={sharedInfoQuery.data.patient} />}
        <TandemCard />
      </Cards>
      <HeadsUpNote items={workItemQueries} />
      {workItemQueries.every((x) => x.data) && <WorkItemsTabs items={workItemQueries} />}
    </div>
  );
};

const HeadsUpNote = (props: { items: UseQueryResult<ItemApiResponse>[] }) => {
  const hasExtraContext = props.items.some((query) => query.data && query.data.item && query.data.item.extra_context);
  if (!hasExtraContext) {
    return null;
  }

  return (
    <HeadsUpContainer>
      <h2>Heads up!</h2>
      {props.items.map((query, index) => {
        if (query.data && query.data.item) {
          const extraContext = query.data.item.extra_context;
          return extraContext ? (
            <div key={index}>
              <strong>{`ERX ${query.data!.drx.erx_id}`}:</strong> {query.data!.item.extra_context}
            </div>
          ) : null;
        }
      })}
    </HeadsUpContainer>
  );
};

const HeadsUpContainer = styled.div`
  padding: 24px;
  background: var(--white);
  border-top: 1px solid var(--grayscale-100);
  white-space: pre-line;
`;

const WorkItemsTabs = (props: { items: UseQueryResult<ItemApiResponse>[] }) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  return (
    <>
      <WorkItemTabContainer>
        {props.items.map((query, index) => (
          <WorkItemTab key={index} $active={index == selectedIndex} onClick={() => setSelectedIndex(index)}>
            {`ERX ${query.data!.drx.erx_id}`}
          </WorkItemTab>
        ))}
      </WorkItemTabContainer>
      {props.items.map((query, index) => (
        <TaskForm key={index} query={query} show={index == selectedIndex} />
      ))}
    </>
  );
};

const WorkItemTabContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0px;
  margin-left: 48px;
`;

const WorkItemTab = styled.div<{ $active: boolean }>`
  color: ${(props) => (props.$active ? 'var(--white)' : 'var(--grayscale-600)')};
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 101%;
  display: flex;
  padding: 12px 24px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 1px;
  border: 1px solid var(--grayscale-100);
  border-right: none;
  border-bottom: none;
  &:last-of-type {
    border-right: 1px solid var(--grayscale-100);
    border-top-right-radius: 12px;
  }

  &:first-of-type {
    border-top-left-radius: 12px;
  }

  background: ${(props) => (props.$active ? 'var(--grayscale-900)' : 'var(--grayscale-50)')};
  cursor: ${(props) => (props.$active ? 'inherit' : 'pointer')};
  width: 100px;
`;
export default WorkItems;
